import { useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { Appbar } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppbarBackButton, OutlineButton, PrimaryButton, ScreenAppbar } from '@/components/atoms';
import { MeetingDetail } from '@/components/organisms';
import { useTranslation } from '@/i18n';
import { spacing } from '@/theme';

interface MeetingDetailPageProps {
  readonly id: string;
  readonly initialPickerVisible?: boolean;
}

const FOOTER_GAP = 10;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    flexDirection: 'row',
    gap: FOOTER_GAP,
    paddingHorizontal: spacing[4],
  },
  footerButton: {
    flex: 1,
  },
});

export const MeetingDetailPage = ({ id, initialPickerVisible }: MeetingDetailPageProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const saveHandlerRef = useRef<(() => void) | null>(null);
  const [keyboardVisible, setKeyboardVisible] = useState(false);

  useEffect(() => {
    const showSub = Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true));
    const hideSub = Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const handleSavePress = useCallback(() => {
    saveHandlerRef.current?.();
  }, []);

  const handleBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }
    router.navigate('/(drawer)/(tabs)/meetings');
  }, [router]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <StatusBar style="light" />
      <ScreenAppbar variant="dark">
        <AppbarBackButton onPress={handleBack} testID="meeting-back-button" variant="dark" />
        <Appbar.Content
          title={t('meetings.detail.headerTitle', { defaultValue: 'Meeting details' })}
        />
      </ScreenAppbar>
      <MeetingDetail
        key={id}
        id={id}
        saveHandlerRef={saveHandlerRef}
        initialPickerVisible={initialPickerVisible}
        onSaved={handleBack}
      />
      <View
        testID="meeting-detail-footer"
        style={[
          styles.footer,
          {
            paddingBottom:
              Platform.OS === 'ios' && keyboardVisible
                ? spacing[1]
                : Math.max(insets.bottom, spacing[4]),
          },
        ]}>
        <View style={styles.footerButton}>
          <OutlineButton onPress={handleBack} testID="meeting-cancel-button">
            {t('meetings.detail.cancel', { defaultValue: 'Cancel' })}
          </OutlineButton>
        </View>
        <View style={styles.footerButton}>
          <PrimaryButton onPress={handleSavePress} testID="meeting-save-button">
            {t('meetings.detail.save', { defaultValue: 'Save' })}
          </PrimaryButton>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};
